import { takeLatest, put, call, select, take } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import {
  getCampaignsIfNeeded,
  getPaginatedCampaignsIfNeeded,
  getAdvancedSearchPaginatedCampaignsIfNeeded,
  getCampaignIfNeeded,
  createCampaignFromCurrent,
  updateCampaignFromCurrent,
  deleteCampaignsByIdList,
  getAssociatedCampaignsFor,
  getAllActiveCampaignsIfNeeded
} from './sagas';
import {
  GET_CAMPAIGNS,
  GET_PAGINATED_CAMPAIGNS,
  GET_ADVANCED_SEARCH_PAGINATED_CAMPAIGNS,
  GET_CAMPAIGN,
  CREATE_CAMPAIGN,
  CREATING_CAMPAIGN_SUCCEEDED,
  UPDATE_CAMPAIGN,
  UPDATING_CAMPAIGN_SUCCEEDED,
  DELETE_CAMPAIGNS,
  getCampaigns,
  getPaginatedCampaigns,
  GETTING_CAMPAIGN_FAILED,
  GETTING_CAMPAIGNS_FAILED,
  CREATING_CAMPAIGN_FAILED,
  UPDATING_CAMPAIGN_FAILED,
  DELETING_CAMPAIGNS_FAILED,
  GET_ASSOCIATED_CAMPAIGNS_FOR,
  GET_ALL_ACTIVE_CAMPAIGNS
} from './actions';
import { actions as UserActions } from '../user';
import { actions as userMessaging } from '../userMessaging';

const currentBusinessContextId = store =>
  store.user.selectedBusinessContext?.businessContextId;

/* Helper Sagas */
function* forceGetAllCampaigns() {
  yield put(getCampaigns({ force: true }));
  yield put(getPaginatedCampaigns({ force: true }));
}

function* forceGetNewCampaigns() {
  yield put(getCampaigns({ force: true }));
  const currentCampaign = yield select(store => store.campaign.current);
  yield put(push(`/campaigns/edit/${currentCampaign.uuid}`));
}

function* waitForUserReady() {
  const userReady = yield select(store => store.user.ready);
  if (!userReady) {
    yield take(UserActions.LOGIN_READY);
  }
}

function* CampaignGetAssociatedFor() {
  yield takeLatest(GET_ASSOCIATED_CAMPAIGNS_FOR, function*(action) {
    yield getAssociatedCampaignsFor(action);
  });
}

/* APP FLOW - Campaigns */

function* AdvancedPaginatedCampaignGetManySaga() {
  yield takeLatest(GET_ADVANCED_SEARCH_PAGINATED_CAMPAIGNS, function*(action) {
    yield call(waitForUserReady);
    yield call(getAdvancedSearchPaginatedCampaignsIfNeeded, action);
  });
}

function* CampaignGetManySaga() {
  yield takeLatest(GET_CAMPAIGNS, function*(action) {
    yield call(waitForUserReady);
    let businessContextId = yield select(currentBusinessContextId);

    if (!businessContextId) {
      yield put(
        userMessaging.setMessage({
          title: 'Error',
          severity: 'error',
          instructions: `This tenant does not have a property. Please contact your system administrator`,
          buttons: [{ title: 'Logout', action: 'LOGOUT' }]
        })
      );
      return;
    }
    yield call(getCampaignsIfNeeded, action);
  });
}
function* PaginatedCampaignGetManySaga() {
  yield takeLatest(GET_PAGINATED_CAMPAIGNS, function*(action) {
    yield call(waitForUserReady);
    yield call(getPaginatedCampaignsIfNeeded, action);
  });
}
function* CampaignGetSingleSaga() {
  yield takeLatest(GET_CAMPAIGN, function*(action) {
    yield call(waitForUserReady);
    yield call(getCampaignIfNeeded, action);
  });
}

function* CampaignHandleFailuresSaga() {
  yield takeLatest(
    [
      GETTING_CAMPAIGN_FAILED,
      GETTING_CAMPAIGNS_FAILED,
      CREATING_CAMPAIGN_FAILED,
      UPDATING_CAMPAIGN_FAILED,
      DELETING_CAMPAIGNS_FAILED
    ],
    function*(action) {
      yield put(
        userMessaging.setMessageForFailedAction({
          type: action.type,
          error: action.payload || action.error
        })
      );
    }
  );
}

function* CampaignGetAllSaga() {
  yield takeLatest(GET_ALL_ACTIVE_CAMPAIGNS, function*(action) {
    yield call(getAllActiveCampaignsIfNeeded, action);
  });
}

// brutally refresh to campaign list on any modification - this *could* be made to act just on the in-memory reducer instead.
function* CampaignCreateSaga() {
  yield waitForUserReady();
  yield takeLatest(CREATE_CAMPAIGN, createCampaignFromCurrent);
}
function* CampaignCreateSuccessSaga() {
  yield takeLatest(CREATING_CAMPAIGN_SUCCEEDED, forceGetNewCampaigns);
}
function* CampaignUpdateSaga() {
  yield waitForUserReady();
  yield takeLatest(UPDATE_CAMPAIGN, updateCampaignFromCurrent);
}
function* CampaignUpdateSuccessSaga() {
  yield takeLatest(UPDATING_CAMPAIGN_SUCCEEDED, forceGetAllCampaigns);
}
function* CampaignDeleteSaga() {
  yield waitForUserReady();
  yield takeLatest(DELETE_CAMPAIGNS, function*(action) {
    yield call(deleteCampaignsByIdList, action);
  });
}

export default [
  CampaignHandleFailuresSaga,
  CampaignGetManySaga,
  PaginatedCampaignGetManySaga,
  CampaignGetSingleSaga,
  CampaignCreateSaga,
  CampaignCreateSuccessSaga,
  CampaignUpdateSaga,
  CampaignUpdateSuccessSaga,
  CampaignDeleteSaga,
  CampaignGetAssociatedFor,
  CampaignGetAllSaga,
  AdvancedPaginatedCampaignGetManySaga
];
